import React from 'react';

interface LogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'full' | 'mark-only' | 'horizontal';
}

export const Logo: React.FC<LogoProps> = ({
  className = '',
  size = 'md',
  variant = 'horizontal'
}) => {
  const sizeClasses = {
    sm: 'h-9',
    md: 'h-11',
    lg: 'h-16',
    xl: 'h-24'
  };

  if (variant === 'mark-only') {
    return (
      <div className={`relative inline-flex items-center justify-center ${sizeClasses[size]} aspect-square ${className}`}>
        <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full drop-shadow-sm">
          <defs>
            <linearGradient id="gearGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#38bdf8" />
              <stop offset="45%" stopColor="#0284c7" />
              <stop offset="100%" stopColor="#1e3a8a" />
            </linearGradient>
            <linearGradient id="waterGrad" x1="0%" y1="0%" x2="100%" y2="50%">
              <stop offset="0%" stopColor="#e0f2fe" />
              <stop offset="50%" stopColor="#38bdf8" />
              <stop offset="100%" stopColor="#0284c7" />
            </linearGradient>
            <linearGradient id="metalGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f1f5f9" />
              <stop offset="50%" stopColor="#94a3b8" />
              <stop offset="100%" stopColor="#475569" />
            </linearGradient>
          </defs>

          {/* Outer Gear Teeth (12 teeth) */}
          <path
            d="M 100 8 
               L 109 8 L 112 22 L 126 26 L 136 17 L 144 23 L 140 37 L 152 45 L 165 40 L 170 49 L 160 59 L 168 73 L 182 74 L 183 84 L 170 91 L 173 105 L 187 111 L 184 121 L 171 123 L 169 137 L 180 147 L 173 155 L 160 152 L 153 164 L 160 176 L 151 182 L 140 173 L 128 181 L 130 195 L 120 197 L 114 184 L 100 185 L 94 197 L 84 195 L 86 181 L 74 173 L 63 182 L 54 176 L 61 164 L 54 152 L 41 155 L 34 147 L 45 137 L 43 123 L 30 121 L 27 111 L 41 105 L 44 91 L 31 84 L 32 74 L 46 73 L 54 59 L 44 49 L 49 40 L 62 45 L 74 37 L 70 23 L 78 17 L 88 26 L 102 22 Z"
            fill="url(#gearGrad)"
            stroke="#0369a1"
            strokeWidth="2.5"
            strokeLinejoin="round"
          />

          {/* Inner Circle cutout */}
          <circle cx="100" cy="100" r="62" fill="#ffffff" stroke="#0284c7" strokeWidth="3" />

          {/* Crossed Wrench 1 (Silver Chrome Combination Wrench) */}
          <g transform="translate(100, 95) rotate(-35) translate(-100, -95)">
            <rect x="94" y="60" width="12" height="70" rx="3" fill="url(#metalGrad)" stroke="#334155" strokeWidth="1" />
            {/* Open End Jaw */}
            <path d="M 91 58 C 88 50, 95 42, 100 42 C 105 42, 112 50, 109 58 L 104 54 L 96 54 Z" fill="url(#metalGrad)" stroke="#334155" strokeWidth="1" />
            {/* Ring End */}
            <circle cx="100" cy="132" r="10" fill="url(#metalGrad)" stroke="#334155" strokeWidth="1" />
            <circle cx="100" cy="132" r="5" fill="#ffffff" />
          </g>

          {/* Crossed Wrench 2 (Pipe Wrench with Red Handle) */}
          <g transform="translate(100, 95) rotate(40) translate(-100, -95)">
            <rect x="94" y="58" width="12" height="72" rx="3" fill="#dc2626" stroke="#991b1b" strokeWidth="1.2" />
            {/* Jaw */}
            <path d="M 88 56 L 88 44 L 112 44 L 112 52 L 106 52 L 106 56 Z" fill="url(#metalGrad)" stroke="#334155" strokeWidth="1.2" />
            <rect x="91" y="47" width="18" height="4" fill="#64748b" />
          </g>

          {/* Water Splash Wave Across Bottom */}
          <path
            d="M 40 115 
               Q 65 95, 90 112 
               T 135 110 
               Q 150 120, 160 112 
               L 160 145 
               Q 130 162, 100 162 
               Q 65 162, 40 145 Z"
            fill="url(#waterGrad)"
            opacity="0.95"
          />
          {/* Water Drops */}
          <circle cx="70" cy="98" r="3" fill="#38bdf8" />
          <circle cx="128" cy="94" r="3.5" fill="#38bdf8" />
          <circle cx="145" cy="102" r="2.5" fill="#0284c7" />
          <circle cx="56" cy="110" r="2" fill="#0284c7" />

          {/* Slogan Arc Text Path */}
          <path id="sloganCurve" d="M 52,100 A 48,48 0 0,1 148,100" fill="none" />
          <text fontSize="8.5" fontWeight="700" fill="#dc2626" letterSpacing="0.4">
            <textPath href="#sloganCurve" startOffset="50%" textAnchor="middle">
              purify life one drop at a time
            </textPath>
          </text>
        </svg>
      </div>
    );
  }

  if (variant === 'full') {
    return (
      <div className={`flex flex-col items-center text-center ${className}`}>
        <div className="relative w-28 h-28 drop-shadow-md">
          <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
            <defs>
              <linearGradient id="gearGradF" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#38bdf8" />
                <stop offset="50%" stopColor="#0284c7" />
                <stop offset="100%" stopColor="#1e3a8a" />
              </linearGradient>
              <linearGradient id="waterGradF" x1="0%" y1="0%" x2="100%" y2="50%">
                <stop offset="0%" stopColor="#e0f2fe" />
                <stop offset="50%" stopColor="#38bdf8" />
                <stop offset="100%" stopColor="#0284c7" />
              </linearGradient>
              <linearGradient id="metalGradF" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f8fafc" />
                <stop offset="50%" stopColor="#94a3b8" />
                <stop offset="100%" stopColor="#475569" />
              </linearGradient>
            </defs>

            {/* Gear Outline */}
            <path
              d="M 100 8 L 109 8 L 112 22 L 126 26 L 136 17 L 144 23 L 140 37 L 152 45 L 165 40 L 170 49 L 160 59 L 168 73 L 182 74 L 183 84 L 170 91 L 173 105 L 187 111 L 184 121 L 171 123 L 169 137 L 180 147 L 173 155 L 160 152 L 153 164 L 160 176 L 151 182 L 140 173 L 128 181 L 130 195 L 120 197 L 114 184 L 100 185 L 94 197 L 84 195 L 86 181 L 74 173 L 63 182 L 54 176 L 61 164 L 54 152 L 41 155 L 34 147 L 45 137 L 43 123 L 30 121 L 27 111 L 41 105 L 44 91 L 31 84 L 32 74 L 46 73 L 54 59 L 44 49 L 49 40 L 62 45 L 74 37 L 70 23 L 78 17 L 88 26 L 102 22 Z"
              fill="url(#gearGradF)"
              stroke="#0369a1"
              strokeWidth="2.5"
            />
            <circle cx="100" cy="100" r="62" fill="#ffffff" stroke="#0284c7" strokeWidth="3" />

            {/* Silver wrench */}
            <g transform="translate(100, 95) rotate(-35) translate(-100, -95)">
              <rect x="94" y="60" width="12" height="70" rx="3" fill="url(#metalGradF)" stroke="#334155" strokeWidth="1" />
              <path d="M 91 58 C 88 50, 95 42, 100 42 C 105 42, 112 50, 109 58 L 104 54 L 96 54 Z" fill="url(#metalGradF)" stroke="#334155" strokeWidth="1" />
              <circle cx="100" cy="132" r="10" fill="url(#metalGradF)" stroke="#334155" strokeWidth="1" />
              <circle cx="100" cy="132" r="5" fill="#ffffff" />
            </g>

            {/* Red pipe wrench */}
            <g transform="translate(100, 95) rotate(40) translate(-100, -95)">
              <rect x="94" y="58" width="12" height="72" rx="3" fill="#dc2626" stroke="#991b1b" strokeWidth="1.2" />
              <path d="M 88 56 L 88 44 L 112 44 L 112 52 L 106 52 L 106 56 Z" fill="url(#metalGradF)" stroke="#334155" strokeWidth="1.2" />
              <rect x="91" y="47" width="18" height="4" fill="#64748b" />
            </g>

            {/* Splash */}
            <path
              d="M 40 115 Q 65 95, 90 112 T 135 110 Q 150 120, 160 112 L 160 145 Q 130 162, 100 162 Q 65 162, 40 145 Z"
              fill="url(#waterGradF)"
              opacity="0.95"
            />

            {/* Slogan */}
            <path id="sloganCurveF" d="M 52,100 A 48,48 0 0,1 148,100" fill="none" />
            <text fontSize="8.5" fontWeight="700" fill="#dc2626">
              <textPath href="#sloganCurveF" startOffset="50%" textAnchor="middle">
                purify life one drop at a time
              </textPath>
            </text>
          </svg>
        </div>
        <div className="mt-2 leading-none">
          <div className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-sky-500 to-blue-700 bg-clip-text text-transparent">
            SAINTS
          </div>
          <div className="text-xs font-bold tracking-wider text-rose-600 uppercase">
            TECH SOLUTIONS
          </div>
        </div>
      </div>
    );
  }

  // Horizontal variant (default for navbar & header)
  return (
    <div className={`inline-flex items-center gap-2.5 ${className}`}>
      <div className={`relative ${sizeClasses[size]} aspect-square shrink-0`}>
        <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full drop-shadow-sm">
          <defs>
            <linearGradient id="gearGradH" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#38bdf8" />
              <stop offset="45%" stopColor="#0284c7" />
              <stop offset="100%" stopColor="#1e3a8a" />
            </linearGradient>
            <linearGradient id="waterGradH" x1="0%" y1="0%" x2="100%" y2="50%">
              <stop offset="0%" stopColor="#e0f2fe" />
              <stop offset="50%" stopColor="#38bdf8" />
              <stop offset="100%" stopColor="#0284c7" />
            </linearGradient>
            <linearGradient id="metalGradH" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f8fafc" />
              <stop offset="50%" stopColor="#94a3b8" />
              <stop offset="100%" stopColor="#475569" />
            </linearGradient>
          </defs>

          {/* Gear Teeth */}
          <path
            d="M 100 8 L 109 8 L 112 22 L 126 26 L 136 17 L 144 23 L 140 37 L 152 45 L 165 40 L 170 49 L 160 59 L 168 73 L 182 74 L 183 84 L 170 91 L 173 105 L 187 111 L 184 121 L 171 123 L 169 137 L 180 147 L 173 155 L 160 152 L 153 164 L 160 176 L 151 182 L 140 173 L 128 181 L 130 195 L 120 197 L 114 184 L 100 185 L 94 197 L 84 195 L 86 181 L 74 173 L 63 182 L 54 176 L 61 164 L 54 152 L 41 155 L 34 147 L 45 137 L 43 123 L 30 121 L 27 111 L 41 105 L 44 91 L 31 84 L 32 74 L 46 73 L 54 59 L 44 49 L 49 40 L 62 45 L 74 37 L 70 23 L 78 17 L 88 26 L 102 22 Z"
            fill="url(#gearGradH)"
            stroke="#0369a1"
            strokeWidth="2"
          />
          <circle cx="100" cy="100" r="62" fill="#ffffff" stroke="#0284c7" strokeWidth="2.5" />

          {/* Tools */}
          <g transform="translate(100, 95) rotate(-35) translate(-100, -95)">
            <rect x="94" y="60" width="12" height="70" rx="3" fill="url(#metalGradH)" stroke="#334155" strokeWidth="1" />
            <path d="M 91 58 C 88 50, 95 42, 100 42 C 105 42, 112 50, 109 58 L 104 54 L 96 54 Z" fill="url(#metalGradH)" stroke="#334155" strokeWidth="1" />
            <circle cx="100" cy="132" r="10" fill="url(#metalGradH)" />
            <circle cx="100" cy="132" r="5" fill="#ffffff" />
          </g>
          <g transform="translate(100, 95) rotate(40) translate(-100, -95)">
            <rect x="94" y="58" width="12" height="72" rx="3" fill="#dc2626" stroke="#991b1b" strokeWidth="1.2" />
            <path d="M 88 56 L 88 44 L 112 44 L 112 52 L 106 52 L 106 56 Z" fill="url(#metalGradH)" stroke="#334155" strokeWidth="1.2" />
          </g>

          {/* Wave */}
          <path
            d="M 40 115 Q 65 95, 90 112 T 135 110 Q 150 120, 160 112 L 160 145 Q 130 162, 100 162 Q 65 162, 40 145 Z"
            fill="url(#waterGradH)"
            opacity="0.95"
          />

          {/* Slogan */}
          <path id="sloganCurveH" d="M 52,100 A 48,48 0 0,1 148,100" fill="none" />
          <text fontSize="8.5" fontWeight="700" fill="#dc2626">
            <textPath href="#sloganCurveH" startOffset="50%" textAnchor="middle">
              purify life one drop at a time
            </textPath>
          </text>
        </svg>
      </div>

      <div className="flex flex-col text-left leading-none">
        <span className="text-lg md:text-xl font-black tracking-tight bg-gradient-to-r from-sky-600 to-blue-800 bg-clip-text text-transparent">
          SAINTS
        </span>
        <span className="text-[10px] md:text-[11px] font-bold tracking-widest text-rose-600 uppercase">
          TECH SOLUTIONS
        </span>
      </div>
    </div>
  );
};
